import { Checkbox, Typography, Tooltip, Chip } from "@mui/material";
import { AlertCircle, Clock, CheckCircle } from "lucide-react";
import {
  SourcesContainer,
  SourcesList,
  SourceItem,
  SourceItemContent,
  SourceItemText,
  StyledBadge,
} from "./styles";
import {
  FontAccessLevel,
  FontAccessLevelText,
  SearchType,
  SearchTypeText,
} from "types/enums";
import { IAvailableExternalFontViewModel } from "viewModels";

interface FontListProps {
  fonts: IAvailableExternalFontViewModel[];
  selectedFonts: string[];
  onFontToggle?: (fontKey: string) => void;
  searchTerm?: string;
  showSelectedOnly?: boolean;
  showMetrics?: boolean;
  extraFonts?: string[];
}

const FontList = ({
  fonts,
  selectedFonts,
  onFontToggle,
  searchTerm = "",
  showSelectedOnly = false,
  showMetrics = true,
  extraFonts = [],
}: FontListProps) => {
  const getTypeBadgeColor = (type: SearchType) => {
    switch (type) {
      case SearchType.BOTH:
        return "#42aa47";
      case SearchType.CNPJ:
        return "#db427a";
      case SearchType.CPF:
        return "#4aa1cf";
      default:
        return "#9E9E9E";
    }
  };

  const getAccessLevelBadgeColor = (type: FontAccessLevel) => {
    switch (type) {
      case FontAccessLevel.PUBLIC:
        return "#44aa49";
      case FontAccessLevel.PRIVATE:
        return "#d81b1b";
      default:
        return "#9E9E9E";
    }
  };

  const displayedFonts = showSelectedOnly
    ? fonts.filter((font) => selectedFonts.includes(font.key))
    : fonts;

  return (
    <SourcesContainer>
      {displayedFonts.length === 0 ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "300px",
            padding: "16px",
            width: "100%",
          }}
        >
          <AlertCircle size={24} color="#D81B60" />
          <Typography
            variant="body1"
            color="text.secondary"
            style={{ marginTop: "8px" }}
          >
            Nenhuma fonte disponível
          </Typography>
        </div>
      ) : (
        <SourcesList style={{ width: "100%" }}>
          {displayedFonts.map((font) => {
            const isSelected = selectedFonts.includes(font.key);
            const isExtra =
              extraFonts.includes(font.key) &&
              font.accessLevel === FontAccessLevel.PUBLIC;

            return (
              <SourceItem
                key={font.key}
                selected={isSelected}
                onClick={() => !font.disabled && onFontToggle?.(font.key)}
                style={{
                  // Destaque visual para extras
                  border: isExtra ? "1px solid #D81B60" : undefined,
                  boxShadow: isExtra
                    ? "0 0 0 2px rgba(216,27,96,0.15)"
                    : undefined,
                  opacity: font.disabled ? 0.6 : 1,
                  cursor:
                    font.disabled || !onFontToggle ? "default" : "pointer",
                }}
              >
                <SourceItemContent>
                  {onFontToggle && (
                    <Checkbox
                      checked={isSelected}
                      onChange={() => !font.disabled && onFontToggle(font.key)}
                      onClick={(e) => e.stopPropagation()}
                      size="small"
                      disabled={font.disabled}
                      sx={{
                        "&.Mui-checked": { color: "#293778" },
                      }}
                    />
                  )}
                  <div
                    style={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      gap: "4px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <SourceItemText>{font.name}</SourceItemText>

                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        {font.disabled ? (
                          <Tooltip
                            title={font.disabledReason || "Indisponível"}
                          >
                            <StyledBadge backgroundColor="#d32f2f">
                              <span>Indisponível</span>
                            </StyledBadge>
                          </Tooltip>
                        ) : (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "4px",
                              alignItems: "flex-end",
                            }}
                          >
                            {/* Tipo (CPF/CNPJ/Ambos) */}
                            <StyledBadge
                              backgroundColor={getTypeBadgeColor(font.type)}
                            >
                              <span>{SearchTypeText[font.type]}</span>
                            </StyledBadge>

                            {/* Access Level */}
                            <StyledBadge
                              backgroundColor={getAccessLevelBadgeColor(
                                font.accessLevel
                              )}
                            >
                              <span>
                                {FontAccessLevelText[font.accessLevel]}
                              </span>
                            </StyledBadge>
                          </div>
                        )}
                      </div>
                    </div>

                    {showMetrics &&
                      (font.averageExecutionTimeSeconds ||
                        font.successRate) && (
                        <div
                          style={{
                            display: "flex",
                            gap: "12px",
                            marginTop: "2px",
                          }}
                        >
                          {font.averageExecutionTimeSeconds && (
                            <Tooltip title="Tempo médio de execução">
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                <Clock size={12} style={{ marginRight: 4 }} />
                                {font.averageExecutionTimeSeconds}s
                              </Typography>
                            </Tooltip>
                          )}
                          {font.successRate !== undefined && (
                            <Tooltip title="Taxa de sucesso">
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                <CheckCircle
                                  size={12}
                                  style={{ marginRight: 4 }}
                                />
                                {font.successRate}%
                              </Typography>
                            </Tooltip>
                          )}
                        </div>
                      )}
                  </div>
                </SourceItemContent>
              </SourceItem>
            );
          })}
        </SourcesList>
      )}
    </SourcesContainer>
  );
};

export default FontList;
